import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input } from '@/components/common'
import { ultraadminBillingApi, type BillingSettings } from '@/services/api/billing'
import { useToast } from '@/components/ui/ToastProvider'

type VatRow = { country_code: string; rate_percent: number | string }

function Card({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="text-sm text-gray-500 mt-1 mb-4">{description}</p>
      {children}
    </div>
  )
}

export function BillingSettingsSection({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast()
  const [s, setS] = useState<BillingSettings | null>(null)
  const [vat, setVat] = useState<VatRow[]>([])
  const [saving, setSaving] = useState(false)
  const [vatCountry, setVatCountry] = useState('CH')
  const [vatRate, setVatRate] = useState('8.1')

  const load = () => {
    ultraadminBillingApi.getSettings().then(setS).catch(() => showToast({ message: 'Zahlungseinstellungen konnten nicht geladen werden.', type: 'error', duration: 4000 }))
    ultraadminBillingApi.vatRates().then((r) => setVat(r as VatRow[])).catch(() => undefined)
  }
  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (s && window.location.hash === '#zahlung') {
      document.getElementById('zahlung')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [s])

  if (!s) return null

  const patch = (k: keyof BillingSettings, v: string | number | boolean) => setS({ ...s, [k]: v })

  const saveCore = async () => {
    setSaving(true)
    try {
      const saved = await ultraadminBillingApi.putSettings({
        ...s,
        due_days: Number(s.due_days),
        reminder_2_days: Number(s.reminder_2_days),
        lock_days_after_reminder_2: Number(s.lock_days_after_reminder_2),
        debit_retry_hours: Number(s.debit_retry_hours),
        ticket_pause_days: Number(s.ticket_pause_days),
      })
      setS(saved)
      showToast({ message: 'Zahlungseinstellungen gespeichert.', type: 'success', duration: 3000 })
    } catch (err: unknown) {
      const detail = (err as { detail?: string })?.detail || 'Speichern fehlgeschlagen.'
      showToast({ message: String(detail), type: 'error', duration: 5000 })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div id="zahlung" className={embedded ? '' : 'mt-10'}>
      {!embedded && (
        <>
          <h2 className="text-lg font-bold text-gray-900 mb-1">Zahlung</h2>
          <p className="text-sm text-gray-600 mb-6">
            Plattform-Rechnung an die Schule. Das Rechnungs-PDF nutzt das E-Mail-/Dokumenten-Logo oben, nicht das Sidebar-Logo.
          </p>
        </>
      )}

      <Card
        title="Wer steht auf der Rechnung?"
        description="Absender der Plattform auf PDF und Swiss QR. Eine IBAN in V1. Schulen können das nicht ändern."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Firma" value={s.legal_name || ''} onChange={(e) => patch('legal_name', e.target.value)} />
          <Input label="MWST-Nr." value={s.vat_number || ''} onChange={(e) => patch('vat_number', e.target.value)} />
          <Input label="Adresse" value={s.address || ''} onChange={(e) => patch('address', e.target.value)} />
          <Input label="PLZ" value={s.zip_code || ''} onChange={(e) => patch('zip_code', e.target.value)} />
          <Input label="Ort" value={s.city || ''} onChange={(e) => patch('city', e.target.value)} />
          <Input label="Land (ISO-2)" value={s.country || ''} onChange={(e) => patch('country', e.target.value.toUpperCase())} />
          <Input label="IBAN" value={s.iban || ''} onChange={(e) => patch('iban', e.target.value)} />
          <Input label="QR-BESR / QR-IID (optional)" value={s.qr_besr_id || ''} onChange={(e) => patch('qr_besr_id', e.target.value)} />
        </div>
      </Card>

      <Card
        title="Wann wird gemahnt und gesperrt?"
        description="Fälligkeit ab Rechnungsdatum. Hinweis 1 am Fälligkeitstag, Hinweis 2 danach mit Sperrdatum. Auto-Sperre setzt school.is_active=false. Schulen ändern diese Werte nicht."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Fälligkeit (Tage)" type="number" value={String(s.due_days)} onChange={(e) => patch('due_days', Number(e.target.value))} />
          <Input label="Zweiter Hinweis (Tage nach Hinweis 1)" type="number" value={String(s.reminder_2_days)} onChange={(e) => patch('reminder_2_days', Number(e.target.value))} />
          <Input label="Sperre (Tage nach Hinweis 2)" type="number" value={String(s.lock_days_after_reminder_2)} onChange={(e) => patch('lock_days_after_reminder_2', Number(e.target.value))} />
          <Input label="Debit-Retry (Stunden)" type="number" value={String(s.debit_retry_hours)} onChange={(e) => patch('debit_retry_hours', Number(e.target.value))} />
          <Input label="Widerspruch-Pause N (Tage)" type="number" value={String(s.ticket_pause_days)} onChange={(e) => patch('ticket_pause_days', Number(e.target.value))} />
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
            <input type="checkbox" checked={!!s.auto_lock_enabled} onChange={(e) => patch('auto_lock_enabled', e.target.checked)} />
            Auto-Sperre aktiv
          </label>
        </div>
      </Card>

      <p className="text-sm text-gray-600 mb-6">
        Listenpreise und Dollar-Kopplung: <Link className="text-indigo-700 underline" to="/ultraadmin/prices">Listenpreise</Link>.
        Schul-Rabatte: <Link className="text-indigo-700 underline" to="/ultraadmin/school-terms">Schul-Konditionen</Link>.
      </p>

      <Card
        title="Welcher MWST-Satz gilt je Land?"
        description="Listen und Deals sind Brutto. Satz nach school.country. 0 wenn nicht steuerbar. Kein Reverse-Charge in V1."
      >
        <ul className="text-sm mb-4 space-y-1">
          {vat.map((v) => (
            <li key={v.country_code} className="flex justify-between border-b py-1">
              <span>{v.country_code}</span>
              <span>{v.rate_percent} %</span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <Input label="Land (ISO-2)" value={vatCountry} onChange={(e) => setVatCountry(e.target.value.toUpperCase())} />
          <Input label="Satz %" value={vatRate} onChange={(e) => setVatRate(e.target.value)} />
          <Button
            type="button"
            size="sm"
            onClick={() => ultraadminBillingApi.putVat(vatCountry, Number(vatRate)).then(() => { load(); showToast({ message: 'MWST gespeichert.', type: 'success', duration: 2500 }) })}
          >
            Satz speichern
          </Button>
        </div>
      </Card>

      <Button type="button" onClick={() => void saveCore()} disabled={saving}>
        {saving ? 'Speichert…' : 'Absender und Fristen speichern'}
      </Button>
    </div>
  )
}
